<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/AppIcon.vue'
import { api, formatApiError, type GlobalSearchGroup, type GlobalSearchResult } from '@/lib/api'
import { useDialogFocus } from '@/lib/dialog'
import { auth } from '@/stores/auth'
import type { Permission } from '@/types'

interface NavItem {
  to: string
  label: string
  description: string
  icon: string
  permissions: Permission[]
}

const navItems: NavItem[] = [
  { to: '/command', label: '指挥中心', description: '实时态势与风险', icon: 'dashboard', permissions: ['dashboard:read'] },
  { to: '/content', label: '内容工作台', description: '地图与知识编排', icon: 'map', permissions: ['content:read'] },
  { to: '/agent', label: 'Agent Studio', description: '策略、评测与追踪', icon: 'bot', permissions: ['agent:read', 'vocabulary:read'] },
  { to: '/operations', label: '运营中心', description: '素材与增长体系', icon: 'sparkles', permissions: ['operations:read'] },
  { to: '/people', label: '用户 · 行程 · 设备', description: '统一时间线', icon: 'users', permissions: ['people:read', 'device:read'] },
  { to: '/governance', label: '平台治理', description: '服务、权限与审计', icon: 'shield', permissions: ['health:read', 'provider:read', 'cost:read', 'audit:read', 'ota:read', 'admin:manage'] },
]

const router = useRouter()
const route = useRoute()
const collapsed = ref(false)
const mobileOpen = ref(false)
const isMobile = ref(false)
const mobileSidebar = ref<HTMLElement | null>(null)
const searchOpen = ref(false)
const searchTerm = ref('')
const searchDialog = ref<HTMLElement | null>(null)
const searchState = ref<'idle' | 'loading' | 'ready' | 'error'>('idle')
const searchResults = ref<GlobalSearchResult[]>([])
const searchError = ref('')
const activeSearchIndex = ref(-1)
const theme = ref<'light' | 'dark'>('light')
let searchTimer: number | undefined
let searchGeneration = 0
let mobileMediaQuery: MediaQueryList | undefined

useDialogFocus(searchOpen, searchDialog)
useDialogFocus(mobileOpen, mobileSidebar)

const visibleNav = computed(() => navItems.filter((item) => auth.canAny(item.permissions)))
const pageTitle = computed(() => String(route.meta.title ?? 'Localike Atlas'))
const globalSearchScopes = computed(() => ({
  content: auth.can('content:read'),
  // 用户和行程结果可能暴露个人轨迹与归属，只允许敏感域权限发起请求。
  people: auth.can('people:sensitive:read'),
  devices: auth.can('device:read'),
}))
const globalSearchEnabled = computed(() => Object.values(globalSearchScopes.value).some(Boolean))
const globalSearchLabel = computed(() => {
  const labels = [
    globalSearchScopes.value.content ? '路线、POI' : '',
    globalSearchScopes.value.people ? '行程、用户' : '',
    globalSearchScopes.value.devices ? '设备' : '',
  ].filter(Boolean)
  return `搜索${labels.join('、')}`
})
const groupedSearchResults = computed(() => {
  const labels: Record<GlobalSearchGroup, string> = { content: '内容', trips: '行程', users: '用户', devices: '设备' }
  return (Object.keys(labels) as GlobalSearchGroup[]).flatMap((group) => {
    const items = searchResults.value.filter((item) => item.group === group)
    return items.length ? [{ group, label: labels[group], items }] : []
  })
})

function applyTheme(next: 'light' | 'dark'): void {
  theme.value = next
  document.documentElement.dataset.theme = next
  document.documentElement.style.colorScheme = next
  const themeMeta = document.querySelector('meta[name="theme-color"]')
  themeMeta?.setAttribute('content', next === 'dark' ? '#101718' : '#f6f1e8')
  localStorage.setItem('localike-theme', next)
}

function toggleTheme(): void {
  applyTheme(theme.value === 'light' ? 'dark' : 'light')
}

function handleKeydown(event: KeyboardEvent): void {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    searchOpen.value = true
  }
  if (event.key === 'Escape') {
    searchOpen.value = false
    mobileOpen.value = false
  }
}

function handleMobileViewport(event: MediaQueryListEvent | MediaQueryList): void {
  isMobile.value = event.matches
  if (!event.matches) mobileOpen.value = false
}

async function signOut(): Promise<void> {
  await auth.logout()
  await router.push('/login')
}

async function chooseSearchResult(result: GlobalSearchResult): Promise<void> {
  searchOpen.value = false
  await router.push(result.to)
}

function runSearch(): void {
  const result = searchResults.value[activeSearchIndex.value] ?? searchResults.value[0]
  if (result) void chooseSearchResult(result)
}

async function performGlobalSearch(): Promise<void> {
  const term = searchTerm.value.trim()
  if (term.length < 2 || !globalSearchEnabled.value) {
    searchResults.value = []
    searchState.value = 'idle'
    activeSearchIndex.value = -1
    return
  }
  const generation = ++searchGeneration
  searchState.value = 'loading'
  searchError.value = ''
  try {
    const results = await api.globalSearch(term, globalSearchScopes.value)
    if (generation !== searchGeneration) return
    searchResults.value = results
    activeSearchIndex.value = results.length ? 0 : -1
    searchState.value = 'ready'
  } catch (error) {
    if (generation !== searchGeneration) return
    searchResults.value = []
    activeSearchIndex.value = -1
    searchError.value = formatApiError(error, '全局搜索暂时不可用。')
    searchState.value = 'error'
  }
}

function scheduleGlobalSearch(): void {
  if (searchTimer) window.clearTimeout(searchTimer)
  searchTimer = window.setTimeout(() => { void performGlobalSearch() }, 220)
}

function handleSearchKeydown(event: KeyboardEvent): void {
  if (!searchResults.value.length) return
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    activeSearchIndex.value = (activeSearchIndex.value + 1) % searchResults.value.length
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    activeSearchIndex.value = (activeSearchIndex.value - 1 + searchResults.value.length) % searchResults.value.length
  } else if (event.key === 'Enter') {
    event.preventDefault()
    runSearch()
  }
}

watch(() => route.fullPath, () => {
  mobileOpen.value = false
})
watch(searchTerm, scheduleGlobalSearch)
watch(searchOpen, (open) => {
  if (open && searchTerm.value.trim().length >= 2) scheduleGlobalSearch()
  if (!open && searchTimer) window.clearTimeout(searchTimer)
})

onMounted(() => {
  const stored = localStorage.getItem('localike-theme')
  const preferred = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  applyTheme(stored === 'dark' || stored === 'light' ? stored : preferred)
  mobileMediaQuery = window.matchMedia('(max-width: 900px)')
  handleMobileViewport(mobileMediaQuery)
  mobileMediaQuery.addEventListener('change', handleMobileViewport)
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  mobileMediaQuery?.removeEventListener('change', handleMobileViewport)
  if (searchTimer) window.clearTimeout(searchTimer)
})
</script>

<template>
  <div class="app-shell" :class="{ 'app-shell--collapsed': collapsed }">
    <button
      v-if="mobileOpen"
      class="sidebar-scrim"
      type="button"
      tabindex="-1"
      aria-label="关闭导航菜单"
      @click="mobileOpen = false"
    />

    <aside
      ref="mobileSidebar"
      id="primary-navigation"
      class="sidebar"
      :class="{ 'sidebar--mobile-open': mobileOpen }"
      aria-label="主导航"
      :aria-hidden="isMobile && !mobileOpen ? 'true' : undefined"
      :aria-modal="isMobile && mobileOpen ? 'true' : undefined"
      :inert="isMobile && !mobileOpen ? true : undefined"
      :role="isMobile && mobileOpen ? 'dialog' : undefined"
      tabindex="-1"
    >
      <div class="brand-lockup">
        <span class="brand-mark" aria-hidden="true"><AppIcon name="compass" :size="22" /></span>
        <span v-if="!collapsed" class="brand-copy">
          <strong translate="no">Localike</strong>
          <small>Atlas · 2.0</small>
        </span>
        <button class="icon-button mobile-sidebar-close" type="button" aria-label="关闭导航菜单" @click="mobileOpen = false">
          <AppIcon name="close" />
        </button>
      </div>

      <nav class="nav-list">
        <RouterLink
          v-for="item in visibleNav"
          :key="item.to"
          :to="item.to"
          class="nav-item"
          :title="collapsed ? item.label : undefined"
        >
          <span class="nav-item__icon"><AppIcon :name="item.icon" /></span>
          <span v-if="!collapsed" class="nav-item__copy">
            <strong>{{ item.label }}</strong>
            <small>{{ item.description }}</small>
          </span>
        </RouterLink>
      </nav>

      <div class="sidebar__footer">
        <div v-if="!collapsed" class="environment-card">
          <span class="environment-card__pulse" aria-hidden="true" />
          <span>
            <strong>验收环境</strong>
            <small>服务状态见平台治理</small>
          </span>
        </div>
        <button
          class="sidebar-toggle"
          type="button"
          :aria-label="collapsed ? '展开侧边栏' : '收起侧边栏'"
          @click="collapsed = !collapsed"
        >
          <AppIcon :name="collapsed ? 'panel-open' : 'panel-close'" />
          <span v-if="!collapsed">收起导航</span>
        </button>
      </div>
    </aside>

    <div class="shell-main" :inert="isMobile && mobileOpen ? true : undefined" :aria-hidden="isMobile && mobileOpen ? 'true' : undefined">
      <header class="topbar">
        <div class="topbar__title">
          <button class="icon-button mobile-menu" type="button" aria-label="打开导航菜单" aria-controls="primary-navigation" :aria-expanded="mobileOpen" @click="mobileOpen = true">
            <AppIcon name="menu" />
          </button>
          <div>
            <span>工作空间</span>
            <strong>{{ pageTitle }}</strong>
          </div>
        </div>

        <div class="topbar__actions">
          <button v-if="globalSearchEnabled" class="command-search" type="button" aria-label="打开全局搜索" @click="searchOpen = true">
            <AppIcon name="search" />
            <span>{{ globalSearchLabel }}…</span>
            <kbd>⌘ K</kbd>
          </button>
          <button class="icon-button" type="button" :aria-label="theme === 'light' ? '切换深色主题' : '切换浅色主题'" @click="toggleTheme">
            <AppIcon :name="theme === 'light' ? 'moon' : 'sun'" />
          </button>
          <RouterLink
            v-if="auth.can('audit:read')"
            class="icon-button notification-button"
            to="/governance?tab=audit"
            aria-label="查看审计动态"
          >
            <AppIcon name="bell" />
          </RouterLink>
          <details class="profile-menu">
            <summary :aria-label="`打开账户菜单：${auth.displayName.value}，${auth.roleSummary.value}`">
              <span class="avatar" aria-hidden="true">{{ auth.displayName.value.slice(0, 1).toUpperCase() }}</span>
              <span class="profile-menu__copy">
                <strong>{{ auth.displayName.value }}</strong>
                <small>{{ auth.roleSummary.value }}</small>
              </span>
              <AppIcon name="chevron-down" :size="15" />
            </summary>
            <div class="profile-popover">
              <p>{{ auth.user.value?.email }}</p>
              <button type="button" aria-label="安全退出" @click="signOut"><AppIcon name="logout" />安全退出</button>
            </div>
          </details>
        </div>
      </header>

      <main id="main-content" class="page-container" tabindex="-1">
        <RouterView />
      </main>
    </div>

    <Teleport to="body">
      <div v-if="searchOpen" class="modal-backdrop" role="presentation" @mousedown.self="searchOpen = false">
        <section ref="searchDialog" class="search-dialog" role="dialog" aria-modal="true" aria-labelledby="search-dialog-title" tabindex="-1">
          <h2 id="search-dialog-title" class="sr-only">全局搜索</h2>
          <form class="search-dialog__input" role="search" @submit.prevent="runSearch">
            <AppIcon name="search" />
            <label class="sr-only" for="global-search">{{ globalSearchLabel }}</label>
            <input
              type="search"
              id="global-search"
              v-model="searchTerm"
              name="global-search"
              autocomplete="off"
              :placeholder="`${globalSearchLabel}…`"
              role="combobox"
              aria-autocomplete="list"
              aria-controls="global-search-results"
              :aria-expanded="searchResults.length > 0"
              :aria-activedescendant="activeSearchIndex >= 0 ? `global-search-option-${activeSearchIndex}` : undefined"
              @keydown="handleSearchKeydown"
            />
            <button class="icon-button" type="button" aria-label="关闭搜索" @click="searchOpen = false"><AppIcon name="close" /></button>
          </form>
          <div id="global-search-results" class="search-dialog__results" :role="searchResults.length > 0 ? 'listbox' : undefined" :aria-label="searchResults.length > 0 ? '全局搜索结果' : undefined" aria-live="polite">
            <div v-if="searchState === 'idle'" class="search-dialog__state"><AppIcon name="search" /><span>输入至少 2 个字符，仅搜索当前账号有权访问的范围。</span></div>
            <div v-else-if="searchState === 'loading'" class="search-dialog__state" role="status"><span class="spinner" aria-hidden="true" />正在检索真实数据…</div>
            <div v-else-if="searchState === 'error'" class="search-dialog__state search-dialog__state--error" role="alert"><AppIcon name="alert" /><span>{{ searchError }}</span><button class="button button--secondary" type="button" @click="performGlobalSearch">重试</button></div>
            <div v-else-if="searchResults.length === 0" class="search-dialog__state"><AppIcon name="search" /><span>没有匹配的真实记录。</span></div>
            <section v-for="group in groupedSearchResults" v-else :key="group.group" class="search-result-group" role="group" :aria-labelledby="`global-search-group-${group.group}`">
              <h3 :id="`global-search-group-${group.group}`">{{ group.label }}</h3>
              <button
                v-for="result in group.items"
                :id="`global-search-option-${searchResults.indexOf(result)}`"
                :key="result.key"
                class="search-result"
                :class="{ 'search-result--active': activeSearchIndex === searchResults.indexOf(result) }"
                type="button"
                role="option"
                :aria-selected="activeSearchIndex === searchResults.indexOf(result)"
                @mouseenter="activeSearchIndex = searchResults.indexOf(result)"
                @click="chooseSearchResult(result)"
              >
                <span><strong>{{ result.title }}</strong><small>{{ result.description }}</small></span>
                <AppIcon name="chevron-right" />
              </button>
            </section>
          </div>
          <div class="search-dialog__help">
            <span><kbd>↑</kbd><kbd>↓</kbd> 选择 · <kbd>Enter</kbd> 打开</span>
            <span><kbd>Esc</kbd> 关闭</span>
          </div>
        </section>
      </div>
    </Teleport>
  </div>
</template>
